import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Session token management for backup auth
const SESSION_KEY = 'pdxhacks_session';

export const setSessionToken = (token) => {
  if (token) {
    localStorage.setItem(SESSION_KEY, token);
  }
};

export const getSessionToken = () => {
  return localStorage.getItem(SESSION_KEY);
};

export const clearSessionToken = () => {
  localStorage.removeItem(SESSION_KEY);
};

// Add session token to requests as Authorization header backup
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    if (response.data?.session_token) {
      setSessionToken(response.data.session_token);
    }
    return response;
  },
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    if (response.data?.session_token) {
      setSessionToken(response.data.session_token);
    }
    return response;
  },
  logout: async () => {
    clearSessionToken();
    return api.post('/auth/logout');
  },
  getMe: () => api.get('/auth/me'),
  exchangeSession: async (sessionId) => {
    const response = await api.post('/auth/session', { session_id: sessionId });
    if (response.data?.session_token) {
      setSessionToken(response.data.session_token);
    }
    return response;
  },
};

// User APIs
export const userAPI = {
  getProfile: (userId) => api.get(`/users/${userId}`),
  updateProfile: (data) => api.put('/users/profile', data),
  getUsers: (params) => api.get('/users', { params }),
  followUser: (userId) => api.post(`/users/${userId}/follow`),
};

// Project APIs
export const projectAPI = {
  getAll: (params) => api.get('/projects', { params }),
  getOne: (projectId) => api.get(`/projects/${projectId}`),
  create: (data) => api.post('/projects', data),
  update: (projectId, data) => api.put(`/projects/${projectId}`, data),
  delete: (projectId) => api.delete(`/projects/${projectId}`),
  getTags: () => api.get('/tags/projects'),
  bookmark: (projectId) => api.post(`/projects/${projectId}/bookmark`),
};

// Thread APIs (Forum)
export const threadAPI = {
  getAll: (params) => api.get('/threads', { params }),
  getOne: (threadId) => api.get(`/threads/${threadId}`),
  create: (data) => api.post('/threads', data),
  update: (threadId, data) => api.put(`/threads/${threadId}`, data),
  delete: (threadId) => api.delete(`/threads/${threadId}`),
  getTags: () => api.get('/tags/threads'),
  getCategories: () => api.get('/forum/categories'),
  pin: (threadId) => api.post(`/threads/${threadId}/pin`),
  lock: (threadId) => api.post(`/threads/${threadId}/lock`),
  bookmark: (threadId) => api.post(`/threads/${threadId}/bookmark`),
};

// Comment APIs
export const commentAPI = {
  getAll: (targetType, targetId) => api.get(`/comments/${targetType}/${targetId}`),
  create: (targetType, targetId, data) => api.post(`/comments/${targetType}/${targetId}`, data),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
  markSolution: (commentId) => api.post(`/comments/${commentId}/solution`),
};

// Vote APIs
export const voteAPI = {
  vote: (targetType, targetId, vote) => api.post(`/vote/${targetType}/${targetId}`, { vote }),
};

// Event APIs
export const eventAPI = {
  getAll: (params) => api.get('/events', { params }),
  getOne: (eventId) => api.get(`/events/${eventId}`),
  create: (data) => api.post('/events', data),
  update: (eventId, data) => api.put(`/events/${eventId}`, data),
  register: (eventId) => api.post(`/events/${eventId}/register`),
};

// Resource APIs
export const resourceAPI = {
  getAll: (params) => api.get('/resources', { params }),
  getOne: (resourceId) => api.get(`/resources/${resourceId}`),
  create: (data) => api.post('/resources', data),
};

// Notification APIs
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (notificationIds) => api.post('/notifications/read', { notification_ids: notificationIds }),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Message APIs
export const messageAPI = {
  getConversations: () => api.get('/messages'),
  getMessages: (userId) => api.get('/messages', { params: { user_id: userId } }),
  send: (data) => api.post('/messages', data),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getReports: (params) => api.get('/admin/reports', { params }),
  updateReport: (reportId, data) => api.put(`/admin/reports/${reportId}`, data),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
};

// Report APIs
export const reportAPI = {
  create: (data) => api.post('/report', data),
};

// Search API
export const searchAPI = {
  search: (q, type) => api.get('/search', { params: { q, type } }),
};

// URL Metadata API
export const fetchUrlMetadata = (url) => api.post('/fetch-url-metadata', { url });

// Stats APIs
export const getStats = () => api.get('/stats');
export const getLeaderboard = (params) => api.get('/leaderboard', { params });
export const getBadges = () => api.get('/badges');

// File upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export default api;
